/* eslint-disable max-lines */
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  DocumentResponse,
  Folder,
  PermissionType
} from '@utils/types/folders/folder.ts';
import { useDeleteFolder } from '@utils/api/folders/use-delete-folder.tsx';
import { PATHS } from '../../router/paths.ts';
import toast from 'react-hot-toast';
import { useDeleteTranscriptSession } from '@utils/api/sessions/use-delete-transcript-session.tsx';
import { formatDate, formatDuration } from '@utils/date.ts';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  CopyType,
  DocumentOptions
} from '@components/workspace/document-options.tsx';
import { capitalizeFirstLetter } from '@utils/capitalize.ts';
import { getStatusColors, TONE_COLORS } from '@utils/constants/status-map';
import { useUpdateParentFolder } from '@utils/api/folders/use-update-parent-folder.tsx';
import { useTranslation } from 'react-i18next';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { EditFolderForm } from '@components/workspace/edit-folder-form.tsx';
import { getDocumentRaw } from '@utils/api/documents/use-get-document-raw.tsx';
import { rememberReturnFolder } from '@utils/editors/return-to-folder.ts';
import { liveEditorPath } from '@utils/api/realtime/live-transcript.ts';
import {
  getDocumentDirectUrl,
  triggerDownloadFromUrl
} from '@utils/api/documents/use-get-document-direct.tsx';
import { parse } from '@utils/extensions/content-disposition.extension.ts';
import saveAs from 'file-saver';
import { useGetTranscriptSession } from '@utils/api/sessions/use-get-transcript-session.tsx';
import DownloadDocumentForm from '@components/workspace/download-document-form.tsx';
import EditFilenameForm from '@components/workspace/edit-filename-form.tsx';
import ExportDocumentForm from '@components/workspace/export-document-form.tsx';
import { CopyFolderForm } from '@components/workspace/copy-modal-form.tsx';
import { DuplicateFolderModal } from '@components/workspace/duplicate-folder-modal.tsx';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { ValidationError } from 'yup';
import { enumFallbackLanguages } from '@utils/types/languages.ts';
import { TeamPermission } from '@components/workspace/create-folder.tsx';
import {
  Copy,
  ExternalLink,
  FileText,
  FolderOpen,
  FolderPlus,
  LockIcon,
  PenLine,
  Pin,
  Share,
  Subtitles,
  Trash2
} from 'lucide-react';
import Loader from '@components/atoms/loaders/loader.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';
import cn from 'classnames';
import {
  getPinnedItemsFromPreferences,
  getPinnedItemsKey,
  serializePinnedItems,
  type PinnedItem
} from '@utils/localStorage/pinned-items-storage.ts';
import { useGetUserPreferences } from '@utils/api/organizations/user-preferences/use-get-user-preferences.tsx';
import { useUpsertUserPreference } from '@utils/api/organizations/user-preferences/use-upsert-user-preference.tsx';
import { useGetPinnedItems } from '@utils/api/folders/use-get-pinned-items.tsx';
import { useGetSessionProgress } from '@utils/api/sessions/use-get-session-progress';
import {
  ProgressBar,
  rendersProgressBar
} from '@components/atoms/ui-kit/progress-bar';
import { useFolderTree } from '@utils/api/folders/use-folder-tree';
import { flattenFolderTree } from '@utils/folders/folder-tree';
import { useWorkspaceDnd } from './use-workspace-dnd';
import { canMoveRows, rowKey } from './workspace-dnd-rules';
import { WorkspaceDropArea } from './workspace-drop-area';

type Permission = {
  // null = the folder's default/"shared" permission (no specific team)
  group_id: number | null;
  permission: number;
};

export type WorkspaceRow = {
  id?: string;
  can_delete?: boolean | null;
  select?: boolean;
  icon?: string; // 'folder' | 'Transcript' | 'Caption'
  name?: string;
  duration?: string;
  status?: string;
  language?: string;
  type?: string; // 'Transcript' | 'Caption'
  session_id?: string;
  session?: Session;
  document?: DocumentResponse;
  variant?: 'folder' | 'document';
  last_modified?: string;
  date_upload?: string;
  folder?: Folder | null;
  permissions?: Permission[];
  parent_id?: number | null;
  date?: Date;
  media_url?: string;
  diarization?: boolean | string;
  session_mode?: 'decode' | 'align' | 'upload' | null;
  finished?: boolean;
};

interface WorkspaceProps {
  fetchData: {
    data:
      | {
          result?: {
            can_delete?: boolean | null;
            child_folders?: Folder[];
            folders?: Folder[];
            documents?: DocumentResponse[];
          };
          total_results?: number;
        }
      | undefined;
    isLoading: boolean;
    isFetching?: boolean;
  };
  title: string;
  caption: string;
  children?: ReactNode;
  folderId?: string | undefined;
}

export const Workspace = (props: WorkspaceProps) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // Check if we're on the first page
  const currentOffset = Number(searchParams.get('offset') || '0');
  const isFirstPage = currentOffset === 0;

  // Fetch user preferences for pinned items
  const { data: userPreferencesData, refetch: refetchPreferences } =
    useGetUserPreferences();
  const upsertPreferenceMutation = useUpsertUserPreference();
  const userPreferences = userPreferencesData?.result;

  // Get pinned items for this workspace
  const workspaceId = props.folderId || 'root';
  const pinnedItems = getPinnedItemsFromPreferences(
    userPreferences,
    workspaceId
  );

  // Tracks the in-flight pinned list so rapid clicks accumulate correctly
  // instead of each reading the same stale server state.
  const latestPinnedRef = useRef<PinnedItem[]>(pinnedItems);
  useEffect(() => {
    latestPinnedRef.current = pinnedItems;
  }, [pinnedItems]);

  // Fetch full data for pinned items (only on first page)
  const { data: pinnedItemsData, deletedItemIds } = useGetPinnedItems(
    pinnedItems,
    isFirstPage && pinnedItems.length > 0
  );

  // A pinned workspace has one preference row. Upserting avoids a duplicate-key
  // error after the first pin and keeps the value current for unpinning too.
  const updatePreference = async (key: string, value: string) => {
    await upsertPreferenceMutation.mutateAsync({ key, value });
  };

  // Helper to reset to page 1 when pinning/unpinning
  const resetToFirstPage = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('offset', '0');
    navigate(url.pathname + url.search, { replace: true });
  };

  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteFolderModal, setDeleteFolderModal] = useState(false);
  const [deleteDocumentModal, setDeleteDocumentModal] = useState(false);
  const [selectedToMove, setSelectedToMove] = useState<Folder>();
  const { data: folderTree } = useFolderTree();
  const folderTargets = useMemo(
    () => (folderTree ? flattenFolderTree(folderTree) : undefined),
    [folderTree]
  );
  const currentFolderId = props.folderId ? Number(props.folderId) : null;
  const canMoveToFolder = (rows: WorkspaceRow[], targetId: number | null) =>
    canMoveRows(rows, targetId, currentFolderId, folderTargets);
  const [isEditDocumentModalOpen, setIsEditDocumentModalOpen] = useState(false);
  const [selectedCopyType, setSelectedCopyType] = useState<
    CopyType | undefined
  >(undefined);
  const [selectedItem, setSelectedItem] = useState<WorkspaceRow>();
  const { data: sessionFile } = useGetTranscriptSession(
    selectedItem?.session?.session_id
  );

  const [selectedFilename, setSelectedFilename] = useState(selectedItem?.name);
  const [folderEditId, setFolderEditId] = useState('');
  const [modal, setModal] = useState<'' | 'download' | 'txt' | 'pdf' | 'html'>(
    ''
  );

  const [modalTranscript, setModalTranscript] = useState<boolean>(false);
  const [documentSelectedId, setDocumentSelectedId] = useState('');
  const [sessionSelectedId, setSessionSelectedId] = useState('');
  const [includeSpeakers, setIncludeSpeakers] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(true);
  const [copyFolderModalOpen, setCopyFolderModalOpen] = useState(false);
  const [duplicateFolderModalOpen, setDuplicateFolderModalOpen] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [teamPermissions, setTeamPermissions] = useState<TeamPermission[]>([
    { groupId: '', permission: PermissionType.WRITE }
  ]);

  const [selectedCopyId, setSelectedCopyId] = useState<string | undefined>(
    undefined
  );

  const { data: supportedLanguages } = useGetModels();
  const { data: progressMap } = useGetSessionProgress();

  // Mutations
  const { mutateAsync: deleteFolderMutation } = useDeleteFolder();
  const { mutate: deleteDocumentsMutation } = useDeleteTranscriptSession();
  const { mutateAsync: updateParentFolderMutation } = useUpdateParentFolder();

  const handleCellClick = (row: WorkspaceRow, field: string) => {
    setSelectedItem(row);
    setSelectedFilename(row.name);
    const isFolder = row.variant === 'folder';

    if (field === 'icon' || field === 'name') {
      if (isFolder) {
        navigate(PATHS(row.id).FOLDER_CONTENT);
        return;
      }

      if (row.document?.live_transcript && row.session?.session_id) {
        rememberReturnFolder(row.id, props.folderId ?? null);
        navigate(liveEditorPath(row.session.session_id));
        return;
      }

      const isError =
        String(row.status || '')
          .trim()
          .toLowerCase() === 'error';
      if (isError) {
        toast.error(
          t('document-has-error') ||
            'This file has an error and cannot be opened.',
          { id: 'document-error' }
        );
        return;
      }

      if (!row.session) return;

      // Check if session is not finished yet (processing, queued, etc.)
      const sessionStatus = row.session?.status?.toLowerCase() || '';
      if (sessionStatus !== 'finished') {
        const toastId = toast.loading(t('document-not-ready'));
        setTimeout(() => toast.dismiss(toastId), 4000);
        return;
      }

      // Remember which folder we came from so the editor's back arrow can
      // return there instead of the workspace root (#2035). Persists for the
      // tab session; keyed on document id so multiple open tabs don't clash.
      rememberReturnFolder(row.id, props.folderId ?? null);

      if (row.type === 'Caption') {
        setSelectedCopyId('caption');
        navigate(PATHS(row.session.session_id, row.id).CAPTIONS);
      } else if (row.type === 'Transcript') {
        setSelectedCopyId('transcript');
        navigate(PATHS(row.session.session_id, row.id).TRANSCRIPT);
      } else {
        navigate(PATHS().HOME);
      }
    } else {
      if (isFolder) {
        setFolderEditId(row.id as string);
      } else {
        setDocumentSelectedId(row.id as string);
        setSessionSelectedId(row.session_id as string);
      }
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderEditId || selectedItem?.can_delete !== true || isDeleting) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteFolderMutation([Number(folderEditId)]);
      setDeleteFolderModal(false);
    } catch {
      // The mutation shows the API error. Keep the folder and dialog in place.
    } finally {
      setIsDeleting(false);
    }
  };

  /* --------- Language helpers ---------- */
  const extendedSupportedLanguages = useMemo(() => {
    const existingKeys = new Set(supportedLanguages?.result?.map(l => l.key));
    const fallbackArray = Object.entries(enumFallbackLanguages).map(
      ([key, name]) => ({ key, name })
    );
    const combined = [
      ...(supportedLanguages?.result ?? []),
      ...fallbackArray.filter(f => !existingKeys.has(f.key))
    ];
    return combined;
  }, [supportedLanguages]);

  const knownLangKeys = useMemo(
    () =>
      new Set((extendedSupportedLanguages ?? []).map(l => l.key.toLowerCase())),
    [extendedSupportedLanguages]
  );

  const extractLangKey = (raw?: string) => {
    if (!raw) return undefined;
    const tokens = raw
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter(Boolean);
    const known = tokens.find(tok => knownLangKeys.has(tok));
    if (known) return known;
    return tokens.find(t => t.length === 2 || t.length === 3);
  };

  /* ---------------- Rows ---------------- */
  const rows = useMemo<WorkspaceRow[]>(() => {
    if (props.fetchData.isLoading) return [];

    // Helper function to convert a folder to WorkspaceRow
    const folderToRow = (folder: Folder): WorkspaceRow => ({
      icon: 'folder',
      name: folder.name,
      variant: 'folder',
      id: String(folder.id),
      can_delete: folder.can_delete,
      date: new Date(folder.last_modified),
      parent_id: folder.parent_id,
      permissions: folder.permissions?.map(p => ({
        group_id: p.group_id ?? null,
        permission: p.permission
      })),
      folder: folder.child_folders?.[0] || null,
      date_upload: folder.date_upload ? formatDate(folder.date_upload) : '',
      last_modified: folder.last_modified
        ? formatDate(folder.last_modified)
        : '',
      duration: '',
      status: '',
      language: '',
      type: '',
      diarization: ''
    });

    // Get folders from current page data
    const folders: WorkspaceRow[] =
      (
        props?.fetchData?.data?.result?.child_folders ??
        props?.fetchData?.data?.result?.folders
      )?.map(folderToRow) ?? [];

    // Only merge pinned folders on the first page
    let allFolders: WorkspaceRow[];
    if (isFirstPage) {
      // Merge with pinned folders (from separate fetch)
      const pinnedFoldersRows: WorkspaceRow[] =
        pinnedItemsData?.folders?.map(folderToRow) ?? [];

      // Remove duplicates: if a pinned folder is already in current page, don't duplicate it
      const currentFolderIds = new Set(folders.map(f => f.id));
      const uniquePinnedFolders = pinnedFoldersRows.filter(
        pf => !currentFolderIds.has(pf.id)
      );

      // Combine: pinned folders first, then current page folders
      allFolders = [...uniquePinnedFolders, ...folders];
    } else {
      // On other pages, just use the current page folders
      allFolders = folders;
    }

    // Helper function to convert a document to WorkspaceRow
    const documentToRow = (
      document: DocumentResponse & {
        finalized?: boolean;
        finished?: boolean;
        session?: DocumentResponse['session'] & { finalized?: boolean };
      }
    ): WorkspaceRow => {
      const languageValue =
        document?.language ||
        document?.session?.language ||
        document?.session?.language_key ||
        '';

      let normalizedKey: string | undefined;
      try {
        normalizedKey = extractLangKey?.(languageValue);
      } catch {
        normalizedKey = undefined;
      }

      const languageData = normalizedKey
        ? extendedSupportedLanguages.find(
            l => l.key.toLowerCase() === normalizedKey!.toLowerCase()
          )
        : undefined;

      const fullName =
        languageData?.name ||
        (normalizedKey ? enumFallbackLanguages[normalizedKey] : undefined) ||
        languageValue ||
        '-';

      const isFinalized =
        document?.finished === true || // top-level flag
        document?.finalized === true || // alternate top-level flag (if your API ever adds it)
        document?.session?.finalized === true;

      return {
        icon: document.type === 'caption' ? 'Caption' : 'Transcript',
        name: document.filename,
        date: new Date(document.last_modified),
        duration: formatDuration(document?.session?.duration || 0),
        status: capitalizeFirstLetter(
          document?.session?.status || 'Processing'
        ),
        language: fullName,
        type: document.type === 'caption' ? 'Caption' : 'Transcript',
        session_id: document?.session?.session_id,
        session: document?.session,
        id: document.id,
        variant: 'document',
        document,
        diarization: document?.session?.diarization ? 'Yes' : 'No',

        finished: !!isFinalized,

        date_upload: document?.session?.date_upload
          ? formatDate(document.session.date_upload || '')
          : '',
        last_modified: document?.last_modified
          ? formatDate(document.last_modified || '')
          : document?.session?.last_modified
            ? formatDate(document.session.last_modified || '')
            : '',
        session_mode:
          document?.session_mode || document?.session?.session_mode || null
      };
    };

    // Get documents from current page data
    const documents: WorkspaceRow[] =
      props?.fetchData?.data?.result?.documents?.map(documentToRow) ?? [];

    // Only merge pinned documents on the first page
    let allDocuments: WorkspaceRow[];
    if (isFirstPage) {
      // Merge with pinned documents (from separate fetch)
      const pinnedDocumentsRows: WorkspaceRow[] =
        pinnedItemsData?.documents?.map(documentToRow) ?? [];

      // Remove duplicates: if a pinned document is already in current page, don't duplicate it
      const currentDocumentIds = new Set(documents.map(d => d.id));
      const uniquePinnedDocuments = pinnedDocumentsRows.filter(
        pd => !currentDocumentIds.has(pd.id)
      );

      // Combine: pinned documents first, then current page documents
      allDocuments = [...uniquePinnedDocuments, ...documents];
    } else {
      // On other pages, just use the current page documents
      allDocuments = documents;
    }

    // Use allFolders (which includes pinned folders from separate fetch)
    const combined = [...allFolders, ...allDocuments];

    // When sort=type is active, the backend returns folders and documents in
    // separate arrays (child_folders[], documents[]) even though the union
    // query is 3-way sorted server-side. Our concat forces folders to the
    // top of every page, which breaks the per-page ordering — especially on
    // boundary pages where captions and folders both appear. Re-sort the
    // combined list by a synthesized type key so the visible order matches
    // what the DB produced. Fixes #2035.
    //
    // Priority (asc): caption(0) → folder(1) → transcript(2). Numeric
    // priorities are more robust than localeCompare on the type strings
    // when a row's row.type is null/empty for reasons we haven't accounted
    // for — a bad row falls to the end instead of silently landing between
    // known types.
    //
    // Note: the backend returns transcript documents as type="document".
    // The FE maps that to row.type="Transcript", so toLowerCase() matches.
    const activeSort = searchParams.get('sort');
    const activeDirection = searchParams.get('direction') || 'asc';

    const pinnedIds = new Set(pinnedItems.map(p => p.id));

    if (activeSort === 'type') {
      const typePriority = (row: WorkspaceRow): number => {
        if (row.variant === 'folder') return 1;
        const t = String(row.type ?? '').toLowerCase();
        if (t === 'caption') return 0;
        if (t === 'transcript') return 2;
        return 3; // unknown → after everything
      };
      // Single-pass sort: type is the primary key, pinnedness is a tiebreaker
      // within the same type group. Running a separate pinned-first pass after
      // this would hoist pinned transcripts above non-pinned captions, breaking
      // the type grouping (#2035).
      combined.sort((a, b) => {
        const typeCmp = typePriority(a) - typePriority(b);
        const directedTypeCmp = activeDirection === 'desc' ? -typeCmp : typeCmp;
        if (directedTypeCmp !== 0) return directedTypeCmp;
        // Same type: pinned items first (only on page 1)
        if (isFirstPage) {
          const aPin = pinnedIds.has(a.id || '');
          const bPin = pinnedIds.has(b.id || '');
          if (aPin && !bPin) return -1;
          if (!aPin && bPin) return 1;
        }
        return 0;
      });
      return combined;
    }

    // Only apply pinned-first sorting on the first page
    // Note: folders-first/files-first sorting is handled by the backend
    if (isFirstPage) {
      // Sort with pinned items first — Array.sort is stable, so backend order
      // is preserved within each pinnedness group.
      const sorted = combined.sort((a, b) => {
        const aIsPinned = pinnedIds.has(a.id || '');
        const bIsPinned = pinnedIds.has(b.id || '');

        if (aIsPinned && !bIsPinned) return -1;
        if (!aIsPinned && bIsPinned) return 1;

        return 0;
      });

      return sorted;
    }

    // On other pages, return data in backend order
    return combined;
  }, [
    props?.fetchData?.data?.result,
    t,
    extendedSupportedLanguages,
    props.folderId,
    userPreferences,
    pinnedItems,
    pinnedItemsData,
    isFirstPage,
    searchParams
  ]);
  /* ---------------- Chips ---------------- */
  const Chip = ({
    label,
    className,
    style
  }: {
    label: string;
    className?: string;
    style?: React.CSSProperties;
  }) => (
    <Text
      as="span"
      size="xs"
      weight="medium"
      className={cn(
        'inline-flex w-fit max-w-full items-center px-2 py-0.5 rounded-full truncate',
        className
      )}
      style={style}
    >
      {label}
    </Text>
  );

  /* ---------------- Columns (DataGridV2) ---------------- */
  type CellCtx = { row: { original: WorkspaceRow }; getValue: () => unknown };

  const columns: DataGridColumn<WorkspaceRow>[] = useMemo(() => {
    const statusCell = ({ row, getValue }: CellCtx) => {
      if (row.original.variant === 'folder') return null;
      const status = String(getValue() ?? '');
      const { bg, fg } = getStatusColors(status);
      const chipStyle = { backgroundColor: bg, color: fg };

      const chip = <Chip label={status} style={chipStyle} />;
      const sessionId: string | undefined =
        row.original.session?.session_id ?? row.original.session_id;
      const progressItem = sessionId
        ? (progressMap?.get(sessionId) ?? null)
        : null;
      const statusLower = status.toLowerCase();
      const inFlight = statusLower === 'processing' || statusLower === 'queued';

      let content: React.ReactNode;
      if (status === 'Error') {
        const session: Session | undefined = row.original.session;
        const isBadFile = session?.error_code === 'bad_file';
        const displayChip = isBadFile ? (
          <Chip label="BAD FILE" style={chipStyle} />
        ) : (
          chip
        );
        const tooltipLabel = isBadFile
          ? [session?.error_reason?.replace(/_/g, ' '), session?.error]
              .filter(Boolean)
              .join(' — ')
          : [session?.error_category, session?.error_status || session?.error]
              .filter(Boolean)
              .join(' — ');
        content = (
          <Tooltip
            label={
              tooltipLabel ||
              t('document-has-error') ||
              'This file has an error and cannot be opened.'
            }
            placement="top"
          >
            <span>{displayChip}</span>
          </Tooltip>
        );
      } else {
        content = chip;
      }

      // The bar replaces the status text rather than sitting under it: while a
      // session is working the percentage is the more useful of the two, and
      // "Processing" above a moving bar is saying the same thing twice. The
      // label survives as the bar's tooltip and accessible name.
      //
      // rendersProgressBar guards the swap. Replacing the label with a bar that
      // then declines to draw would leave the cell blank, so anything the bar
      // will not render keeps the chip.
      if (
        inFlight &&
        progressItem &&
        rendersProgressBar(progressItem.progress_state)
      ) {
        return (
          <ProgressBar
            pct={progressItem.progress_pct}
            state={progressItem.progress_state}
            label={status}
          />
        );
      }

      return content;
    };

    const typeCell = ({ row, getValue }: CellCtx) => {
      const sessionMode = row.original?.session_mode;

      // If session_mode is 'align', show Align chip instead
      if (sessionMode === 'align') {
        const { bg: alnBg, fg: alnFg } = getStatusColors('alignment');
        return (
          <Chip
            label={t('alignment')}
            style={{ backgroundColor: alnBg, color: alnFg }}
          />
        );
      }

      const type = String(getValue() ?? '');
      if (!type) return null;

      const label =
        type === 'Transcript'
          ? t('transcript')
          : type === 'Caption'
            ? t('caption')
            : type;

      const { bg: typeBg, fg: typeFg } = getStatusColors(type);
      return (
        <Chip
          label={label}
          style={{ backgroundColor: typeBg, color: typeFg }}
        />
      );
    };

    const languageCell = ({ row, getValue }: CellCtx) => {
      const label = getValue();
      if (!label) return null;

      const rawKey =
        row.original?.session?.language_key ||
        row.original?.session?.language ||
        row.original?.document?.language ||
        (typeof row.original?.language === 'string'
          ? row.original.language
          : undefined);

      const normalized = (() => {
        try {
          return extractLangKey?.(rawKey);
        } catch {
          return undefined;
        }
      })();

      const isPrivate = (() => {
        if (!rawKey || !normalized) return false;
        const r = String(rawKey).toLowerCase();
        const n = String(normalized).toLowerCase();
        if (r === n) return false;
        if (!r.includes(n)) return false;

        const isStandardLocale = /^[a-z]{2}-[a-z]{2}$/i.test(r);
        if (isStandardLocale) return false;

        const looksExtended = /(^\d+-)?[a-z]{2,3}(-[a-z0-9]+)+$/i.test(r);
        return looksExtended || r.length > n.length;
      })();

      return (
        <div className="flex items-center gap-1">
          <Chip
            label={String(label)}
            style={{
              backgroundColor: TONE_COLORS.info.bg,
              color: TONE_COLORS.info.fg
            }}
          />
          {isPrivate && (
            <div
              className="inline-flex items-center"
              title={t('private-model')}
              aria-label={t('private-model')}
            >
              <LockIcon
                className="h-3.5 w-3.5"
                style={{ color: TONE_COLORS.warn.fg }}
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      );
    };

    const diarizationCell = ({ row }: CellCtx) => {
      if (row.original.variant === 'folder') return null;
      const hasSpeakerId =
        row.original.diarization === true ||
        String(row.original.diarization).toLowerCase() === 'yes';
      const label = hasSpeakerId ? t('yes') : t('no');

      const { bg: diarBg, fg: diarFg } = getStatusColors(
        hasSpeakerId ? 'success' : 'neutral'
      );
      const chipStyle = { backgroundColor: diarBg, color: diarFg };

      return <Chip label={label} style={chipStyle} />;
    };

    const finishedCell = ({ row, getValue }: CellCtx) => {
      if (row.original.variant === 'folder') return null;

      const v = getValue();
      const isFinalized =
        typeof v === 'boolean' ? v : String(v).toLowerCase() === 'finalized';

      const label = isFinalized ? t('finalized') : t('editable');
      const { bg: finBg, fg: finFg } = getStatusColors(
        isFinalized ? 'finished' : 'processing'
      );
      const chipStyle = { backgroundColor: finBg, color: finFg };

      return <Chip label={label} style={chipStyle} />;
    };

    return [
      {
        id: 'icon',
        accessorKey: 'icon',
        header: () => <span className="sr-only">{t('type')}</span>,
        minWidth: 28,
        maxWidth: 28,
        width: 28,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row, getValue }: CellCtx) => {
          const val = getValue() as string | undefined;

          // A folder is "private" when nobody has default (group_id: null)
          // access, or that default is explicitly NO_ACCESS. Only groups
          // listed in `permissions` can see it. Arbor feedback (#2035): they
          // couldn't tell private from public at a glance because both
          // rendered with the same yellow open-folder icon.
          const permissions = row.original.permissions as
            | { group_id: number | null; permission: PermissionType }[]
            | undefined;
          const isPrivateFolder =
            val === 'folder' &&
            !permissions?.some(
              p =>
                p.group_id === null && p.permission !== PermissionType.NO_ACCESS
            );

          let iconElement = null;
          switch (val) {
            case 'folder':
              iconElement = isPrivateFolder ? (
                <Tooltip label={t('private-folder')}>
                  <span className="relative inline-flex">
                    <FolderOpen
                      className="h-4 w-4 cursor-pointer"
                      style={{ color: TONE_COLORS.neutral.fg }}
                    />
                    <LockIcon
                      className="h-2 w-2 absolute -bottom-0.5 -right-0.5"
                      style={{ color: TONE_COLORS.neutral.fg }}
                      aria-hidden="true"
                    />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip label={t('public-folder')}>
                  <FolderOpen
                    className="h-4 w-4 cursor-pointer"
                    style={{ color: getColour('folderIcon') }}
                  />
                </Tooltip>
              );
              break;
            case 'Transcript':
              iconElement = (
                <FileText className="h-4 w-4 text-primary/80 cursor-pointer" />
              );
              break;
            case 'Caption':
              iconElement = (
                <Subtitles className="h-4 w-4 text-primary/80 cursor-pointer" />
              );
              break;
          }

          return (
            <div className="flex items-center justify-center w-full h-full">
              {iconElement}
            </div>
          );
        }
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: () => <span className="pl-1">{t('name')}</span>,
        maxSize: 450,
        enableSorting: true,
        enableFiltering: true,
        clampLines: 0,
        cell: ({ row, getValue }: CellCtx) => {
          const name = getValue() as string;
          const isError =
            String(row.original.status ?? '').toLowerCase() === 'error';
          const clampStyle: React.CSSProperties = {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            wordBreak: 'break-all',
            maxWidth: '100%'
          };
          if (isError) {
            const session: Session | undefined = row.original.session;
            const isBadFile = session?.error_code === 'bad_file';
            const message = isBadFile
              ? [session?.error_reason?.replace(/_/g, ' '), session?.error]
                  .filter(Boolean)
                  .join(' — ')
              : [
                  session?.error_category,
                  session?.error_status || session?.error
                ]
                  .filter(Boolean)
                  .join(' — ');
            const errorMessage =
              message ||
              t('document-has-error') ||
              'This file has an error and cannot be opened.';
            const tooltipLabel = `${name} — ${errorMessage}`;
            return (
              <Tooltip label={tooltipLabel} placement="top">
                <span
                  className="pl-1"
                  style={{ ...clampStyle, color: TONE_COLORS.danger.fg }}
                >
                  {name}
                </span>
              </Tooltip>
            );
          }
          return (
            <span className="pl-1" style={clampStyle} title={name}>
              {name}
            </span>
          );
        }
      },
      {
        id: 'duration',
        accessorKey: 'duration',
        header: t('duration'),
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false
      },
      {
        id: 'status',
        accessorKey: 'status',
        filterKey: 'status',
        header: t('status'),
        maxWidth: 140,
        enableSorting: false, // Backend doesn't support sorting by status
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq' as const,
        suppressCellTitle: true,
        filterOptions: [
          { label: t('finished'), value: 'finished' },
          { label: t('processing'), value: 'processing' },
          { label: t('error'), value: 'error' },
          { label: t('queued'), value: 'queued' }
        ],
        cell: statusCell
      },
      {
        id: 'language',
        accessorKey: 'language',
        filterKey: 'language',
        header: t('language'),
        maxWidth: 160,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq' as const,
        filterOptions: extendedSupportedLanguages.map(lang => ({
          label: lang.name,
          value: lang.key
        })),
        cell: languageCell
      },
      {
        id: 'type',
        accessorKey: 'type',
        filterKey: 'type',
        header: t('type'),
        maxWidth: 120,
        // Backend supports `sort=type`; enables Martijn's ask to "order by
        // type" without hunting for the icon toggle in the header (#2035).
        enableSorting: true,
        enableFiltering: false,
        filterVariant: 'select',
        serverOperator: 'eq' as const,
        filterOptions: [
          { label: t('transcript'), value: 'transcript' },
          { label: t('caption'), value: 'caption' }
        ],
        cell: typeCell
      },
      {
        id: 'diarization',
        accessorKey: 'diarization',
        filterKey: 'diarization',
        header: t('speaker-id'),
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        serverOperator: 'eq' as const,
        filterOptions: [
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: diarizationCell
      },
      {
        id: 'finished',
        accessorKey: 'finished',
        filterKey: 'finalized',
        header: t('finalized'),
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        serverOperator: 'eq' as const,
        filterOptions: [
          { label: t('finalized'), value: 'true' },
          { label: t('editable'), value: 'false' }
        ],
        cell: finishedCell
      },

      {
        id: 'date_upload',
        accessorKey: 'date_upload',
        header: t('uploaded'),
        maxWidth: 180,
        // Backend has no `date_upload` sort field; the folders API layer
        // aliases sort=date_upload → sort=created before sending, which is
        // the DB timestamp captured when the upload record is created.
        // Semantically equivalent for uploaded files. Fixes #2035.
        enableSorting: true,
        enableFiltering: false,
        filterVariant: 'date'
      },
      {
        id: 'last_modified',
        accessorKey: 'last_modified',
        header: t('last-modified'),
        maxWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'date-range'
      }
    ];
  }, [
    t,
    extendedSupportedLanguages,
    props.folderId,
    userPreferences,
    progressMap
  ]);

  /* ---------------- Selection ops (bulk) ---------------- */
  const moveSelection = async (
    rows: WorkspaceRow[],
    targetId: number | null = selectedToMove?.id ?? null
  ) => {
    if (!canMoveToFolder(rows, targetId)) return;
    const documents = rows.filter(r => r.variant === 'document');
    const folders = rows.filter(r => r.variant === 'folder');

    if (
      documents.length > 0 &&
      props.folderId &&
      props.fetchData.data?.result?.can_delete !== true
    ) {
      toast.error(t('folder-manage-permission-denied'));
      return;
    }

    const restrictedFolder = folders.find(folder => folder.can_delete !== true);
    if (restrictedFolder) {
      toast.error(
        t(
          restrictedFolder.can_delete === false
            ? 'folder-manage-permission-denied'
            : 'folder-manage-permission-unavailable'
        )
      );
      return;
    }

    const docIds = documents.map(d => d.id).filter(Boolean);
    try {
      if (docIds.length > 0) {
        await Promise.all(
          docIds.map(documentId =>
            updateParentFolderMutation(
              {
                ids: [String(documentId)],
                type: 3,
                parent_id: targetId
              },
              {
                onError: () => {
                  toast.error(t('failed-to-move-document-please-try-again'));
                }
              }
            )
          )
        );
      }

      const folderIds = folders.map(f => f.id).filter(Boolean);
      if (folderIds.length > 0) {
        await Promise.all(
          folderIds.map(fid =>
            updateParentFolderMutation(
              {
                ids: [String(fid)],
                type: 1,
                parent_id: targetId
              },
              {
                onError: () => {
                  toast.error(t('failed-to-move-folder-please-try-again'));
                }
              }
            )
          )
        );
      }

      toast.success(t('items-moved-successfully'));
    } catch {
      toast.error(t('an-error-occurred'));
    }
  };

  const dnd = useWorkspaceDnd({
    currentFolderId,
    folders: folderTargets,
    moveRows: moveSelection
  });

  /* ---------------- Contextual Actions (row) ---------------- */
  const actions = useMemo<GridAction<WorkspaceRow>[]>(() => {
    const workspaceId = props.folderId || 'root';

    return [
      {
        id: 'open',
        label: t('view'),
        icon: <ExternalLink className="h-4 w-4" />,
        isVisible: () => true,
        run: row => {
          handleCellClick(row, 'name');
        }
      },
      {
        id: 'copy_session_id',
        label: t('copy-session-id') || 'Copy session ID',
        icon: <Copy className="h-4 w-4" />,
        isVisible: row =>
          row?.variant === 'document' &&
          String(row?.status || '')
            .trim()
            .toLowerCase() === 'error' &&
          !!row?.session_id,
        run: row => {
          navigator.clipboard
            .writeText(row.session_id as string)
            .then(() => {
              toast.success(t('session-id-copied') || 'Session ID copied');
            })
            .catch(() => {
              toast.error(t('copy-failed') || 'Failed to copy');
            });
        }
      },
      {
        id: 'pin',
        label: t('pin-to-top') || 'Pin to top',
        icon: <Pin className="h-3.5 w-3.5" />,
        sticky: true,
        isVisible: row =>
          !latestPinnedRef.current.some(p => p.id === (row?.id || '')),
        run: async row => {
          const before = latestPinnedRef.current;
          const newItem: PinnedItem = {
            id: row.id || '',
            type: (row.variant || 'document') as 'folder' | 'document',
            name: row.name,
            pinnedAt: Date.now(),
            sessionId: row.session_id
          };
          const newPinned = [...before, newItem];
          latestPinnedRef.current = newPinned;

          try {
            await updatePreference(
              getPinnedItemsKey(workspaceId),
              serializePinnedItems(newPinned)
            );
            await refetchPreferences();
            queryClient.invalidateQueries(['pinned-folder']);
            queryClient.invalidateQueries(['pinned-document']);
            toast.success(t('pinned') || 'Pinned');
            setTimeout(() => resetToFirstPage(), 500);
          } catch {
            latestPinnedRef.current = before;
            toast.error(t('an-error-occurred'));
            refetchPreferences().catch(() => {});
          }
        }
      },
      {
        id: 'unpin',
        label: t('unpin') || 'Unpin',
        icon: <Pin className="h-3.5 w-3.5" />,
        sticky: true,
        isActive: () => true,
        isVisible: row =>
          latestPinnedRef.current.some(p => p.id === (row?.id || '')),
        run: async row => {
          const before = latestPinnedRef.current;
          const newPinned = before.filter(p => p.id !== (row.id || ''));
          latestPinnedRef.current = newPinned;

          try {
            await updatePreference(
              getPinnedItemsKey(workspaceId),
              serializePinnedItems(newPinned)
            );
            await refetchPreferences();
            queryClient.invalidateQueries(['pinned-folder']);
            queryClient.invalidateQueries(['pinned-document']);
            toast.success(t('unpinned') || 'Unpinned');
          } catch {
            latestPinnedRef.current = before;
            toast.error(t('an-error-occurred'));
            refetchPreferences().catch(() => {});
          }
        }
      },
      {
        id: 'turn_modified_transcript',
        label: t('turn-into-transcript'),
        icon: <FileText className="h-4 w-4" />,
        isVisible: row =>
          row?.variant === 'document' &&
          row?.type !== 'Transcript' &&
          !!row?.session_id,
        run: row => {
          if (!row.session_id) {
            toast.error(
              t('session-not-found') ||
                'Session not found. Cannot copy document.'
            );
            return;
          }
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          setSelectedCopyId('Modified transcript-editor');
          setIsCopyModalOpen(true);
        }
      },
      {
        id: 'turn_modified_caption',
        label: t('turn-into-subtitles'),
        icon: <Subtitles className="h-4 w-4" />,
        isVisible: row =>
          row?.variant === 'document' &&
          row?.type !== 'Caption' &&
          !!row?.session_id,
        run: row => {
          if (!row.session_id) {
            toast.error(
              t('session-not-found') ||
                'Session not found. Cannot copy document.'
            );
            return;
          }
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          setSelectedCopyId('Modified caption');
          setIsCopyModalOpen(true);
        }
      },
      {
        id: 'turn_original_transcript',
        label: t('turn-original-result-into-transcript'),
        icon: <FileText className="h-4 w-4" />,
        isVisible: row => row?.variant === 'document' && !!row?.session_id,
        run: row => {
          if (!row.session_id) {
            toast.error(
              t('session-not-found') ||
                'Session not found. Cannot copy document.'
            );
            return;
          }
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          setSelectedCopyId('Original transcript-editor');
          setIsCopyModalOpen(true);
        }
      },
      {
        id: 'turn_original_caption',
        label: t('turn-original-result-into-subtitles'),
        icon: <Subtitles className="h-4 w-4" />,
        isVisible: row => row?.variant === 'document' && !!row?.session_id,
        run: row => {
          if (!row.session_id) {
            toast.error(
              t('session-not-found') ||
                'Session not found. Cannot copy document.'
            );
            return;
          }
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          setSelectedCopyId('Original caption');
          setIsCopyModalOpen(true);
        }
      },
      {
        id: 'edit_document_name',
        label: t('edit-document-name'),
        icon: <PenLine className="h-4 w-4" />,
        isVisible: row => row?.variant === 'document',
        run: row => {
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          setIsEditDocumentModalOpen(true);
        }
      },
      {
        id: 'export_document',
        label: t('export-document'),
        icon: <Share className="h-4 w-4" />,
        isVisible: row => row?.variant === 'document',
        run: row => {
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          if (row.icon === 'Caption') setModal('download');
          else setModalTranscript(true);
        }
      },
      {
        id: 'delete_document',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        isVisible: row => row?.variant === 'document',
        run: row => {
          setSelectedItem(row);
          setDocumentSelectedId(row.id as string);
          setSessionSelectedId(row.session_id as string);
          setDeleteDocumentModal(true);
        }
      },
      // Folder actions
      {
        id: 'edit_folder',
        label: t('edit'),
        icon: <PenLine className="h-4 w-4" />,
        isVisible: row => row?.variant === 'folder',
        isDisabled: row =>
          row?.can_delete === false
            ? t('folder-manage-permission-denied')
            : row?.can_delete !== true
              ? t('folder-manage-permission-unavailable')
              : false,
        run: row => {
          setSelectedItem(row);
          setIsEditModalOpen(true);
        }
      },
      {
        id: 'copy_folder',
        label: t('copy'),
        icon: <FolderPlus className="h-4 w-4" />,
        isVisible: row => row?.variant === 'folder',
        run: row => {
          setSelectedItem(row);
          setCopyFolderModalOpen(true);
        }
      },
      {
        id: 'duplicate_folder',
        label: t('duplicate'),
        icon: <Copy className="h-4 w-4" />,
        isVisible: row => row?.variant === 'folder',
        run: row => {
          setSelectedItem(row);
          setDuplicateFolderModalOpen(true);
        }
      },
      {
        id: 'delete_folder',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        isVisible: row => row?.variant === 'folder',
        isDisabled: row =>
          row?.can_delete === false
            ? t('folder-delete-permission-denied')
            : row?.can_delete !== true
              ? t('folder-delete-permission-unavailable')
              : false,
        run: row => {
          setSelectedItem(row);
          setFolderEditId(String(row.id ?? ''));
          setDeleteFolderModal(true);
        }
      }
    ];
  }, [
    t,
    handleCellClick,
    props.folderId,
    userPreferences,
    resetToFirstPage,
    updatePreference,
    refetchPreferences,
    queryClient
  ]);

  /* ---------------- Export helpers ---------------- */
  const handleMediaDownload = () => {
    if (!selectedItem || !sessionFile) {
      toast.error(t('error-downloading-media'));
      return;
    }
    if (!sessionFile?.result.media_url) {
      toast.error(t('no-media-available'));
      return;
    }
    const link = document.createElement('a');
    link.href = sessionFile.result.media_url;
    link.setAttribute(
      'download',
      selectedItem?.session?.filename || 'media-file'
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async (format: string) => {
    if (!selectedItem) {
      toast.error(t('error-downloading-document'));
      return;
    }

    if (format === 'media') {
      handleMediaDownload();
      return;
    }

    try {
      const response = await getDocumentDirectUrl({
        sessionId: selectedItem?.session?.session_id as string,
        documentId: selectedItem?.document?.id as string,
        without_timestamps: !includeTimestamps,
        without_speakers: !includeSpeakers,
        format
      });

      // Trigger download directly from Azure Blobs (bypasses load balancer)
      await triggerDownloadFromUrl(
        response.result.download_url,
        response.result.filename,
        response.result.content_type
      );
      setModal('');

      // Optional: Show success toast
      // toast.success(t('download-started'));
    } catch (error) {
      // Fallback: If direct download fails, use legacy method
      const axiosErr = error as AxiosError<BaseError>;
      if (
        axiosErr?.response?.status === 400 &&
        axiosErr?.response?.data?.error === 'direct-download-not-supported'
      ) {
        // Fallback to legacy streaming method
        getDocumentRaw({
          sessionId: selectedItem?.session?.session_id as string,
          documentId: selectedItem?.document?.id as string,
          without_timestamps: !includeTimestamps,
          without_speakers: !includeSpeakers,
          format
        })
          .then(response => {
            const disposition = parse(
              response.headers['content-disposition'] || ''
            );
            const filename =
              disposition?.parameters?.filename || `download.${format}`;
            saveAs(response.data, filename);
            setModal('');
          })
          .catch(fallbackError => {
            if (fallbackError?.response?.status === 404)
              toast.error(t('document-not-found'));
            else toast.error(t('error-downloading-document'));
          });
      } else {
        // Handle other errors
        if (axiosErr?.response?.status === 404)
          toast.error(t('document-not-found'));
        else toast.error(t('error-downloading-document'));
      }
    }
  };

  /* ---------------- Auto-cleanup deleted pinned items ---------------- */
  // Stable string key so the effect only re-runs when the set of deleted IDs
  // actually changes, not on every render due to a new array reference.
  const deletedItemIdsKey = deletedItemIds.join(',');
  useEffect(() => {
    if (!deletedItemIdsKey || !userPreferences) return;
    const ids = deletedItemIdsKey.split(',');
    const workspaceId = props.folderId || 'root';
    const currentPinnedItems = getPinnedItemsFromPreferences(
      userPreferences,
      workspaceId
    );
    const updatedItems = currentPinnedItems.filter(
      item => !ids.includes(item.id)
    );
    if (updatedItems.length !== currentPinnedItems.length) {
      const key = `pinned_workspace_${workspaceId}`;
      const value = JSON.stringify(updatedItems);
      updatePreference(key, value).catch(() => {});
    }
  }, [deletedItemIdsKey, userPreferences, props.folderId]);

  /* ---------------- Reflect team permissions on select (copy modal) ----
   * EditFolderForm owns its own initialization (with an index-0 "default
   * access" holder). This flat mapping is only for CopyFolderForm, so it
   * must NOT run while editing or it overwrites edit's state and the access
   * selector wrongly shows "Shared" for a private folder. */
  useEffect(() => {
    if (selectedItem && copyFolderModalOpen) {
      setTeamPermissions(
        selectedItem.permissions?.map(p => ({
          groupId: p.group_id?.toString() ?? '',
          permission: p.permission
        })) || []
      );
    }
  }, [selectedItem, copyFolderModalOpen]);

  /* ---------------- Render ---------------- */
  return (
    <WorkspaceDropArea dnd={dnd} header={props.children}>
      {props.fetchData.isLoading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <Loader />
        </div>
      ) : (
        <DataGridV2<WorkspaceRow>
          data={rows}
          columns={columns}
          actions={actions}
          getRowProps={dnd.getRowProps}
          isMoving={dnd.isMoving}
          currentFolderId={currentFolderId ?? undefined}
          canMoveToFolder={canMoveToFolder}
          enableRowSelection
          enableRangeSelection
          onDelete={async () => {
            toast.error(t('delete-items-one-at-a-time'));
          }}
          onMove={async selected => {
            const documents = selected.filter(r => r.variant === 'document');
            const folders = selected.filter(r => r.variant === 'folder');
            await moveSelection([...documents, ...folders]);
          }}
          folders={
            props?.fetchData?.data?.result?.folders ??
            props?.fetchData?.data?.result?.child_folders
          }
          selectedToMove={selectedToMove}
          setSelectedToMove={setSelectedToMove}
          onCellClick={handleCellClick}
          enablePagination
          manualPagination
          enableSorting
          height="68vh"
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={props?.fetchData?.data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          fill
          dense
          striped
          grid={false}
          loading={props.fetchData.isLoading}
          isFetching={props.fetchData.isFetching}
          getRowId={rowKey}
        />
      )}

      {/* Folder edit/copy */}
      {isEditModalOpen && selectedItem && (
        <EditFolderForm
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={selectedItem as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setSelectedItem={setSelectedItem as any}
          teamPermissions={teamPermissions}
          setTeamPermissions={setTeamPermissions}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
        />
      )}
      {copyFolderModalOpen && selectedItem && (
        <CopyFolderForm
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={selectedItem as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setSelectedItem={setSelectedItem as any}
          teamPermissions={teamPermissions}
          setTeamPermissions={setTeamPermissions}
          copyFolderModalOpen={copyFolderModalOpen}
          setCopyFolderModalOpen={setCopyFolderModalOpen}
        />
      )}
      {duplicateFolderModalOpen && selectedItem && (
        <DuplicateFolderModal
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={selectedItem as any}
          isOpen={duplicateFolderModalOpen}
          onClose={() => {
            setDuplicateFolderModalOpen(false);
            setSelectedItem(undefined);
          }}
        />
      )}

      {/* Document convert/export/edit */}
      {isCopyModalOpen && (
        <DocumentOptions
          isEditModalOpen={isCopyModalOpen}
          setIsEditModalOpen={setIsCopyModalOpen}
          selectedCopyId={selectedCopyId}
          selectedCopyType={selectedCopyType}
          setSelectedCopyType={setSelectedCopyType}
          selectedFilename={selectedFilename}
          setSelectedFilename={setSelectedFilename}
          selectedSessionId={sessionSelectedId}
          selectedDocumentId={documentSelectedId}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={selectedItem as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setSelectedItem={setSelectedItem as any}
          folderId={props.folderId}
        />
      )}
      {isEditDocumentModalOpen && (
        <EditFilenameForm
          isEditDocumentModalOpen={isEditDocumentModalOpen}
          setIsEditDocumentModalOpen={setIsEditDocumentModalOpen}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={selectedItem as any}
        />
      )}
      {modal && (
        <DownloadDocumentForm
          handleExport={handleExport}
          modal={modal}
          setModal={setModal}
          includeTimestamps={includeTimestamps}
          setIncludeTimestamps={setIncludeTimestamps}
          includeSpeakers={includeSpeakers}
          setIncludeSpeakers={setIncludeSpeakers}
        />
      )}
      {selectedItem && selectedItem.session_id && selectedItem.id && (
        <ExportDocumentForm
          modalTranscript={modalTranscript}
          setModalTranscript={setModalTranscript}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedItem={selectedItem as any}
          handleMediaDownload={handleMediaDownload}
          includeTimestamps={includeTimestamps}
          setIncludeTimestamps={setIncludeTimestamps}
          includeSpeakers={includeSpeakers}
          setIncludeSpeakers={setIncludeSpeakers}
          sessionId={selectedItem.session_id}
          documentId={selectedItem?.id}
        />
      )}

      {/* Confirmation modals */}
      <ConfirmationModal
        isOpen={deleteFolderModal}
        onClose={() => {
          if (!isDeleting) setDeleteFolderModal(false);
        }}
        size="small"
        title={t('delete-folder-named', { name: selectedItem?.name ?? '' })}
        destructive
        confirmLabel={isDeleting ? t('deleting') : t('delete-folder-action')}
        confirmDisabled={isDeleting || selectedItem?.can_delete !== true}
        onConfirm={handleDeleteFolder}
      >
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-100">
          <Trash2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm leading-6">
            {t('delete-folder-with-contents-warning')}
          </p>
        </div>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={deleteDocumentModal}
        onClose={() => setDeleteDocumentModal(false)}
        size="small"
        title={t('delete-document-confirmation')}
        caption={t(
          'are-you-sure-you-want-to-delete-this-document-this-action-cannot-be-undone'
        )}
        destructive
        confirmLabel={isDeleting ? t('deleting') : t('delete')}
        confirmDisabled={isDeleting}
        onConfirm={async () => {
          if (!selectedItem) return;
          setIsDeleting(true);
          try {
            await new Promise(res => setTimeout(res, 300));
            await new Promise<void>((resolve, reject) =>
              deleteDocumentsMutation(
                {
                  documentId: selectedItem.id as string,
                  sessionId: selectedItem.session_id as string
                },
                {
                  onSuccess: () => {
                    toast.success(t('document-deleted-successfully'));
                    resolve();
                  },
                  onError: (error: unknown) => {
                    // Don't show error for 404 - document already deleted
                    if (error instanceof Error && 'isAxiosError' in error) {
                      const axiosError = error as AxiosError<BaseError>;
                      if (axiosError.response?.status === 404) {
                        resolve(); // Treat as success - document already deleted
                        return;
                      }
                    }

                    // Handle ValidationError (from 422 responses)
                    if (error instanceof ValidationError) {
                      reject(
                        new Error(error.message || t('delete-document-failed'))
                      );
                      return;
                    }

                    // Handle AxiosError
                    if (error instanceof Error && 'isAxiosError' in error) {
                      const axiosError = error as AxiosError<BaseError>;
                      const msg =
                        axiosError?.response?.data?.error_description ||
                        axiosError?.response?.data?.detail ||
                        axiosError?.response?.data?.error ||
                        axiosError?.response?.data?.message ||
                        t('delete-document-failed');
                      reject(new Error(msg));
                      return;
                    }

                    // Fallback
                    reject(new Error(t('delete-document-failed')));
                  }
                }
              )
            );
            setDeleteDocumentModal(false);
          } catch (e) {
            toast.error((e as Error)?.message || t('delete-document-failed'));
            setDeleteDocumentModal(false);
          } finally {
            setIsDeleting(false);
          }
        }}
      />
    </WorkspaceDropArea>
  );
};
